import { useNavigate, useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { faPlain, toman } from 'utilities/helper/format'
import { initialOf, longDateOf } from './format'

type TOutcome = 'accepted' | 'rejected' | 'cancelled'

const OUTCOME: Record<TOutcome, { label: string; chip: string }> = {
    accepted: { label: 'پذیرفته شد', chip: ' bg-success-2 text-success-on' },
    rejected: { label: 'رد شد', chip: ' bg-danger-soft text-danger-on' },
    cancelled: { label: 'لغو شد', chip: ' bg-canvas text-text-secondary' },
}

/**
 * A decided loan request — «پذیرفته‌شده» or «رد یا لغوشده».
 *
 * These rows used to expand into «ساعت تخصیص درخواست ۱۴:۳۲:۰۷» and a «توسط»
 * whose placeholder was misspelt «ثبت نشذه». The one question anybody opens a
 * decided request with is «چه شد و چرا؟», and that answer — the decision, its
 * reason, the thread before it — is the کارتابل, so the card goes there.
 */
export default function ClosedDemandCard({
    item,
}: {
    item: IAcceptedDemand | IRejectedDemandLoan
}) {
    const navigate = useNavigate()
    const { id: fundId } = useParams()

    const outcome: TOutcome =
        item.demand_state === 'acpd'
            ? 'accepted'
            : item.demand_state === 'cnld'
            ? 'cancelled'
            : 'rejected'
    const name = item.poolship?.name ?? ''
    const amount =
        outcome === 'accepted' && item.loan_cashout_amount
            ? item.loan_cashout_amount
            : item.amount
    const decidedAt =
        outcome === 'accepted'
            ? item.acceptance_datetime
            : item.rejection_datetime || item.state_datetime
    const decidedBy =
        outcome === 'accepted'
            ? item.acceptor?.name
            : outcome === 'rejected'
            ? item.rejecter?.name
            : ''

    const open = () => {
        posthog.capture('loan_demand_cartable_opened', {
            fund_id: fundId,
            demand_id: item.id,
            source: `requests_list_${outcome}`,
        })
        navigate(`/fund/loan-request/detail/${item.id}?fund=${fundId}`)
    }

    return (
        <article
            role="button"
            tabIndex={0}
            aria-label={`درخواست وام ${name}، ${OUTCOME[outcome].label}`}
            onClick={open}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    open()
                }
            }}
            className=" cursor-pointer rounded-2xl border border-stroke bg-white p-4 active:bg-canvas"
        >
            <div className=" flex items-start gap-3">
                <span
                    aria-hidden
                    className=" flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-canvas text-sm font-bold text-text-secondary"
                >
                    {initialOf(name)}
                </span>
                <div className=" min-w-0 flex-1">
                    <div className=" flex items-start justify-between gap-2">
                        <p className=" truncate text-sm font-bold text-heading">
                            {name}
                        </p>
                        <span
                            className={` shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${OUTCOME[outcome].chip}`}
                        >
                            {OUTCOME[outcome].label}
                        </span>
                    </div>
                    <p className=" mt-[2px] text-[11px] text-text-muted">
                        {decidedAt ? longDateOf(decidedAt) : 'تاریخ ثبت نشده'}
                        {decidedBy ? `، توسط ${decidedBy}` : ''}
                    </p>
                </div>
            </div>

            <div className=" mt-3 flex items-end justify-between gap-2">
                <div>
                    <p className=" text-sm font-bold text-heading">
                        {toman(amount)}
                    </p>
                    <p className=" mt-[2px] text-[11px] text-text-secondary">
                        {faPlain.format(item.num_repayments)} قسط، درخواست{' '}
                        {longDateOf(item.created)}
                    </p>
                </div>
                <span className=" shrink-0 text-[11px] font-bold text-green-1">
                    سوابق ←
                </span>
            </div>

            {outcome === 'accepted' && item.tag && (
                <p className=" mt-2 rounded-lg bg-canvas px-3 py-2 text-[11px] leading-6 text-text-body">
                    توضیح مدیر: {item.tag}
                </p>
            )}
        </article>
    )
}
